import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper
} from '@mui/material';
import {
  Home,
  Search,
  ArrowBack,
  ErrorOutline
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSearch = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box textAlign="center">
        {/* 404 Icon */}
        <Box sx={{ mb: 4 }}>
          <ErrorOutline 
            sx={{ 
              fontSize: 120, 
              color: 'primary.main',
              opacity: 0.7
            }} 
          />
        </Box>

        {/* 404 Text */}
        <Typography 
          variant="h1" 
          component="h1" 
          sx={{ 
            fontSize: { xs: '4rem', md: '6rem' },
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 2
          }}
        >
          404
        </Typography>

        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom
          sx={{ 
            fontWeight: 600,
            mb: 2
          }}
        >
          {t('notFound.title')}
        </Typography>

        <Typography 
          variant="h6" 
          color="text.secondary" 
          paragraph
          sx={{ mb: 4 }}
        >
          {t('notFound.description')}
        </Typography>

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 6 }}>
          <Grid item>
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{ minWidth: 160 }}
            >
              {t('notFound.goHome')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Search />}
              onClick={handleSearch}
              sx={{ minWidth: 160 }}
            >
              {t('notFound.search')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{ minWidth: 160, color: 'primary.contrastText' }}
            >
              {t('notFound.goBack')}
            </Button>
          </Grid>
        </Grid>

        {/* Helpful Links */}
        <Card sx={{ maxWidth: 600, mx: 'auto' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('notFound.helpfulLinks')}
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => navigate('/')}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('nav.search')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('notFound.searchDescription')}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => navigate('/tips')}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('nav.tips')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('notFound.tipsDescription')}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t('notFound.contactInfo')}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default NotFound;
